import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import type { ApiClient, Anlagenzeile, Anlagendaten } from '../../api';
import { Geraeteaufbau } from '../Schemata';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, OHNE_WERT, PropertyGrid, StatusBadge, WertEinheit, formatiereZahl, type Spalte,
} from '../../muster';
import './lueftung.css';

/**
 * Die Anlagenseite eines Lüftungsgeräts.
 *
 * --- Zwei Rahmen, eine Stildatei (TV7a, TX-STANDARD 1.6) -----------------------
 *
 * Die Seite läuft in ZWEI Rahmen: in der Anwendung mit Kopfzeile und Reitern,
 * und öffentlich über einen signierten Verweis aus Revit — dort ohne beides.
 * Bis TV7a trug sie dafür neun Farbliterale und ihre Stile am Element. Beide
 * Rahmen sind dasselbe Bündel mit denselben Merkmalen (`stil/merkmale.css`);
 * die Stile stehen jetzt in `lueftung.css` (Klassen `lue-anl-*`, das KVS-Bild
 * mit den Klassen der Seite Kreislaufverbund).
 *
 * --- Warum der Aufbau oben steht ----------------------------------------------
 *
 * Ein Lüftungsgerät IST eine Folge von Funktionseinheiten. Wer wissen will, ob
 * ein Register vor oder hinter der Wärmerückgewinnung sitzt, sieht es im Bild
 * sofort — in einer Tabelle mit einer Spalte 'Reihenfolge' nicht.
 *
 * Und die Druckverlustbilanz steht dort mit: Weicht die Summe der Einheiten vom
 * internen Druckverlust ab, den der Ventilator meldet, fehlt eine Einheit im
 * Datenblattauszug. Bei allen fünf robatherm-Dateien war das der Fall.
 */

/**
 * Abbildung der vier Herkunftsfarben auf die drei Herkünfte des
 * `PropertyGrid`. Die Kante bleibt dieselbe fachliche Kennzeichnung wie im
 * Bestand: grün = aus dem Modell, gelb = nur Datenbank, blau = gerechnet.
 * Für „grau" (entwertet) gibt es keine Entsprechung — dort bleibt die Kante
 * weg, und der Hinweis sagt es.
 */
const HERKUNFT_GRID: Record<string, 'modell' | 'daten' | 'baugruppe' | undefined> = {
  gruen: 'modell',
  gelb: 'daten',
  blau: 'baugruppe',
  grau: undefined,
};

/** Was die vier Herkunftsfarben des Backends bedeuten - als Text am Feld. */
const HERKUNFT: Record<string, { text: string }> = {
  gruen: { text: 'aus dem Modell gelesen, dorthin schreibbar' },
  gelb: { text: 'nur in der Datenbank geführt' },
  blau: { text: 'von der Plattform gerechnet' },
  grau: { text: 'als falsch erkannt und entwertet' },
};

/** Eine Zahl des KVS-Bilds deutsch (S1.7) - wie bisher ohne feste Nachkommastellen. */
const zahl = (wert: number) => formatiereZahl(wert);

type Kanalsystem = Anlagendaten['kanalsysteme'][number];
type Register = NonNullable<NonNullable<Anlagendaten['kvs']>['register']>[number];

/** Ein Wert aus dem DataJson eines Betriebspunkts. */
function bp(json: string | null | undefined, schluessel: string) {
  if (!json) return null;
  try {
    const d = JSON.parse(json) as Record<string, string>;
    return d[schluessel] ?? null;
  } catch {
    return null;
  }
}

// ═══ Die Anlagenliste ════════════════════════════════════════════════════════

/**
 * Alle Lüftungsanlagen eines Projekts.
 *
 * Die Zahl der Funktionseinheiten sagt, wo die Datenpflege steht: eine Anlage
 * ohne Einheiten hat kein Datenblatt und damit keine Betriebspunkte — und ohne
 * die kennt die Rohrnetzberechnung ihre Register nicht.
 */
/**
 * Das Zuordnungsbild eines Kreislaufverbunds.
 *
 * Vier Instanzen KVS1–4, je Instanz die Zuluft- und Abluftplätze und dazwischen
 * der Glykolkreis, der beide verbindet. Das IST die Wärmerückgewinnung — sie
 * sitzt nicht in einem Gerät, sondern zwischen zweien.
 *
 * Bewusst OHNE Gerätezuordnung: Welches körperliche Gerät auf welchem Platz
 * sitzt, geht aus den Typdatenblättern nicht hervor. Ein Bild, das Linien zu
 * benannten Geräten zöge, behauptete eine Genauigkeit, die die Quelle nicht
 * hergibt. Gezeigt werden Platz, Volumenstrom und Leistung — alles belegt.
 *
 * Reines SVG ohne Bibliothek, wie der Geräteaufbau daneben: Die Seite läuft
 * auch im öffentlichen Rahmen ohne die Anwendung drumherum.
 */
function KvsBild({ kvs }: { kvs: NonNullable<Anlagendaten['kvs']> }) {
  const plaetze = kvs.geraeteplaetze ?? [];
  if (!plaetze.length) return null;

  const instanzen = [...new Set(plaetze.map((p) => p.anlage))].sort();

  const zeilenHoehe = 74;
  const hoehe = instanzen.length * zeilenHoehe + 26;
  const breite = 640;
  const mitte = breite / 2;

  return (
    <svg viewBox={`0 0 ${breite} ${hoehe}`} width="100%"
         className="lue-kvs-bild lue-anl-kvs"
         role="img"
         aria-label="Zuordnung der Geräteplätze zu den KVS-Instanzen">
      <text x={mitte - 150} y={14} className="lue-kvs-kopf">Zuluft</text>
      <text x={mitte} y={14} className="lue-kvs-kopf">Glykolkreis</text>
      <text x={mitte + 150} y={14} className="lue-kvs-kopf">Abluft</text>

      {instanzen.map((inst, i) => {
        const y = 26 + i * zeilenHoehe;
        const zu = plaetze.filter((p) => p.anlage === inst && p.luftart === 'Zuluft');
        const ab = plaetze.filter((p) => p.anlage === inst && p.luftart === 'Abluft');
        const daten = kvs.instanzen?.find(
          (x) => x.anlage === inst && x.saison === 'Winter');

        // Die Kästchenhöhe teilt sich auf die Plätze auf - vier Abluftplätze
        // werden schmaler als zwei, und das Bild bleibt in der Zeile.
        const kasten = (n: number) => Math.max(9, 44 / Math.max(n, 1) - 3);

        return (
          <g key={inst}>
            <text x={4} y={y + 26} className="lue-anl-kvs-instanz">{inst}</text>
            {daten?.rueckwaermzahlTrockenPct != null && (
              <text x={4} y={y + 40} className="lue-anl-kvs-rueck">
                {zahl(daten.rueckwaermzahlTrockenPct)} %
              </text>
            )}

            {zu.map((p, j) => {
              const h = kasten(zu.length);
              const py = y + 4 + j * (h + 3);
              return (
                <g key={p.geraeteslot} className="lue-kvs-zu">
                  <rect x={64} y={py} width={190} height={h} rx={3} />
                  <text x={70} y={py + h / 2 + 3}>
                    {p.geraeteslot} · {p.volumenstromM3h != null ? zahl(p.volumenstromM3h) : ''} m³/h
                    {p.leistungKw ? ` · ${zahl(p.leistungKw)} kW` : ''}
                  </text>
                </g>
              );
            })}

            {/* Der Glykolkreis: der Pfeil, der beide Seiten verbindet. Ohne
                ihn wäre das Bild zwei Listen nebeneinander. */}
            <line x1={254} y1={y + 26} x2={386} y2={y + 26} className="lue-kvs-kreis" />
            <polygon points={`254,${y + 26} 262,${y + 22} 262,${y + 30}`} className="lue-kvs-pfeil" />
            <text x={320} y={y + 20} className="lue-kvs-kreis-text">
              {daten?.glykolProzent ? `${daten.glykolProzent} % Glykol` : 'Sole'}
            </text>

            {ab.map((p, j) => {
              const h = kasten(ab.length);
              const py = y + 4 + j * (h + 3);
              return (
                <g key={p.geraeteslot} className="lue-kvs-ab">
                  <rect x={386} y={py} width={190} height={h} rx={3} />
                  <text x={392} y={py + h / 2 + 3}>
                    {p.geraeteslot} · {p.volumenstromM3h != null ? zahl(p.volumenstromM3h) : ''} m³/h
                    {p.leistungKw ? ` · ${zahl(p.leistungKw)} kW` : ''}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

export function Anlagenseiten({ api }: { api: ApiClient }) {
  const { id: projektId } = useParams<{ id: string }>();
  const [zeilen, setZeilen] = useState<Anlagenzeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    if (!projektId) return;
    api.anlagenseiten(projektId).then(setZeilen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  const gefiltert = useMemo(() => {
    const t = suche.trim().toLowerCase();
    return (zeilen ?? []).filter((z) =>
      (!nurOffen || !z.einheiten)
      && (!t || [z.anlage, z.aksSchluessel, z.raumnummer, z.raumname,
                 z.geraetebezeichnung, z.fabrikat, z.angebot]
            .some((x) => (x ?? '').toLowerCase().includes(t))));
  }, [zeilen, suche, nurOffen]);

  const ohne = useMemo(
    () => (zeilen ?? []).filter((z) => !z.einheiten).length, [zeilen]);

  const spalten: Spalte<Anlagenzeile>[] = [
    {
      schluessel: 'anlage', titel: 'Anlage', immerSichtbar: true,
      wert: (z) => z.anlage,
      inhalt: (z) => (
        <Link to={`/projekte/${projektId}/anlagen/${z.id}`}>{z.anlage}</Link>
      ),
    },
    {
      schluessel: 'aks', titel: 'AKS', wert: (z) => z.aksSchluessel,
      inhalt: (z) => <span className="lue-leise">{z.aksSchluessel ?? OHNE_WERT}</span>,
    },
    {
      schluessel: 'geraet', titel: 'Gerät',
      wert: (z) => [z.geraetebezeichnung, z.fabrikat, z.angebot].filter(Boolean).join(' '),
      inhalt: (z) => (
        <>
          {z.geraetebezeichnung ?? <span className="lue-leise">{OHNE_WERT}</span>}
          {z.angebot && (
            <div className="lue-anl-zusatz">
              {z.fabrikat} · {z.angebot}
            </div>
          )}
        </>
      ),
    },
    {
      schluessel: 'zuluft', titel: 'Zuluft', art: 'zahl', einheit: 'm³/h',
      wert: (z) => (z.zuluft ? Number(z.zuluft) : null),
    },
    {
      schluessel: 'abluft', titel: 'Abluft', art: 'zahl', einheit: 'm³/h',
      wert: (z) => (z.abluft ? Number(z.abluft) : null),
    },
    {
      // Null Einheiten heißt: kein Datenblatt eingelesen. Das ist der
      // wichtigste Wert der Zeile und deshalb als Zustand ausgewiesen.
      schluessel: 'einheiten', titel: 'Funktionseinheiten', art: 'status',
      breite: '190px', wert: (z) => z.einheiten,
      inhalt: (z) => (z.einheiten
        ? <>{z.einheiten}</>
        : <StatusBadge zustand="warnung" text="kein Datenblatt" klein />),
    },
    {
      schluessel: 'ort', titel: 'Ort',
      wert: (z) => [z.ebene, z.raumnummer].filter(Boolean).join(' · '),
      inhalt: (z) => (
        <span className="lue-leise">
          {z.ebene}{z.raumnummer ? ` · ${z.raumnummer}` : ''}
        </span>
      ),
    },
  ];

  const kopf = (
    <PageHeader
      titel="Anlagenseiten"
      beschreibung={'Alle Lüftungsanlagen des Modells. Die Zahl der '
        + 'Funktionseinheiten sagt, wo die Datenpflege steht: eine Anlage ohne '
        + 'Einheiten hat kein Datenblatt und damit keine Betriebspunkte.'}
      meta={zeilen ? `${zeilen.length} Anlagen · ${ohne} ohne Datenblatt` : undefined}
      aktionen={(
        <button
          className="m-knopf"
          onClick={async () => {
            if (!projektId) return;
            setMeldung('Verweise werden gebildet …');
            try {
              const e = await api.anlagenVerweise(projektId);
              setMeldung(`${e.anlagen} Anlage(n): ${e.neu} neu, ${e.geaendert} `
                       + `geändert, ${e.unveraendert} unverändert. ${e.hinweis}`);
            } catch (x) {
              setMeldung(x instanceof Error ? x.message : String(x));
            }
          }}
          title="Bildet für jede Anlage den öffentlichen Verweis und trägt ihn ein. Beim nächsten Rückschreiben wandert er nach TuV_RLT_anlage_url."
        >
          Verweise eintragen
        </button>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Anlagenseiten konnten nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!zeilen) {
    return (
      <>
        {kopf}
        <LoadingState text="Anlagen werden geladen …" zeilen={10} spalten={6} />
      </>
    );
  }

  return (
    <>
      {kopf}

      {meldung && <p className="lue-meldung" role="status">{meldung}</p>}

      <DataTable
        name="Anlagen"
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(z) => String(z.id)}
        suchbegriff={suche}
        beiSuche={setSuche}
        suchePlatzhalter="Anlage, AKS, Raum, Angebot …"
        trefferText={`${gefiltert.length} von ${zeilen.length} Anlagen`}
        ausfuhr
        ausfuhrDateiname="anlagenseiten"
        werkzeuge={(
          <label className="lue-anl-haken">
            <input type="checkbox" checked={nurOffen}
                   onChange={(e) => setNurOffen(e.target.checked)} />
            nur ohne Datenblatt ({ohne})
          </label>
        )}
        leer={(
          <EmptyState
            titel={nurOffen || suche
              ? 'Keine Anlage passt zu diesem Filter.'
              : 'Für dieses Modell ist noch keine Lüftungsanlage aufgenommen.'}
            erklaerung={nurOffen
              ? 'Jede Anlage trägt ein Datenblatt — das ist die gute Nachricht.'
              : 'Die Anlagen entstehen aus einem Auftrag mit Lüftungsaufnahme.'}
            aktion={(nurOffen || suche) ? (
              <button className="m-knopf" onClick={() => { setNurOffen(false); setSuche(''); }}>
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />
    </>
  );
}

// ═══ Die Seite selbst ════════════════════════════════════════════════════════

/**
 * Der gemeinsame Inhalt beider Rahmen.
 *
 * Intern und öffentlich zeigen dasselbe, bis auf die offenen Punkte der
 * Datenpflege und den Verweis. Wer über den QR-Code kommt, soll die technischen
 * Daten sehen — das ist der Zweck — aber nicht, was die Planung noch zu klären
 * hat.
 */
function Inhalt({ d, intern }: { d: Anlagendaten; intern: boolean }) {
  return (
    <>
      <PageHeader
        titel={d.anlage}
        beschreibung={[
          d.aksSchluessel, d.ebene,
          d.raumnummer ? `${d.raumnummer} ${d.raumname ?? ''}`.trim() : null,
        ].filter(Boolean).join(' · ')}
        meta={d.datenblatt ? (
          <>
            {d.datenblatt.hersteller} · {d.datenblatt.geraetebezeichnung}
            {d.datenblatt.angebotsnummer && <> · Angebot {d.datenblatt.angebotsnummer}</>}
            {d.datenblatt.aktuelleDatenversion && <> · Fassung {d.datenblatt.aktuelleDatenversion}</>}
          </>
        ) : undefined}
      />

      {/* Der Aufbau zuerst: Er sagt am schnellsten, was das Gerät ist. */}
      {d.stroeme?.length ? (
        <div className="lue-anl-aufbau">
          <Geraeteaufbau d={d} />
        </div>
      ) : (
        <EmptyState
          titel="Für diese Anlage ist noch kein Datenblatt eingelesen."
          erklaerung={'Ohne Datenblatt gibt es keinen Geräteaufbau und keine '
            + 'Betriebspunkte — und ohne die kennt die Rohrnetzberechnung ihre '
            + 'Register nicht.'}
        />
      )}

      {/* Die wasserseitigen Betriebspunkte. Sie sind der Teil, der Heizung und
          Kälte betrifft, und der einzige Ort, an dem sichtbar wird, auf welche
          Spreizung ein Register ausgelegt ist. */}
      <Betriebspunkte d={d} />

      {/* Die Anlagendaten in Gruppen wie im Feldkatalog, mit Herkunftsfarbe. */}
      {(d.gruppen ?? []).map((g) => (
        <ContentSection key={g.gruppe} titel={g.gruppe}>
          <PropertyGrid
            spalten={3}
            dicht
            eintraege={g.felder.map((f) => ({
              beschriftung: f.label,
              // Wert und Einheit getrennt (Abschnitt 15): Der Bestand schrieb
              // `${f.wert} ${f.unit}` in EIN Textfeld.
              wert: f.wert,
              einheit: f.unit ?? undefined,
              herkunft: HERKUNFT_GRID[f.farbe],
              hinweis: (HERKUNFT[f.farbe] ?? HERKUNFT.gelb).text,
            }))}
          />
        </ContentSection>
      ))}

      {/* Die Kanalsysteme: an welchen Netzen die Anlage hängt. Ein Verbundnetz
          ist ausdrücklich gekennzeichnet — dort teilen mehrere Anlagen ein
          Netz, und eine Summe je Anlage wäre dann falsch. */}
      {d.kanalsysteme?.length ? (
        <ContentSection titel="Kanalsysteme" nackt>
          <DataTable
            name="Kanalsysteme"
            spalten={[
              {
                schluessel: 'luftart', titel: 'Luftart', breite: '120px',
                wert: (k: Kanalsystem) => k.luftart,
                inhalt: (k: Kanalsystem) => <span className="lue-leise">{k.luftart ?? OHNE_WERT}</span>,
              },
              {
                schluessel: 'systemname', titel: 'Systemname', immerSichtbar: true,
                wert: (k: Kanalsystem) => k.systemname,
              },
              {
                schluessel: 'verbund', titel: 'Verbundnetz', art: 'status',
                breite: '230px',
                wert: (k: Kanalsystem) => (k.istVerbundnetz
                  ? `Verbundnetz mit ${k.anlagenAnzahl} Anlagen` : 'eigenes Netz'),
                inhalt: (k: Kanalsystem) => (k.istVerbundnetz
                  ? (
                    <StatusBadge
                      zustand="warnung" klein
                      text={`Verbundnetz mit ${k.anlagenAnzahl} Anlagen`}
                      titel="Mehrere Anlagen teilen dieses Netz — eine Summe je Anlage wäre falsch."
                    />
                  )
                  : <span className="lue-leise">eigenes Netz</span>),
              },
            ]}
            zeilen={d.kanalsysteme}
            schluessel={(k: Kanalsystem) => String(k.id)}
            suche={false}
            spaltenwahl={false}
            dicht
          />
        </ContentSection>
      ) : null}

      {/* --- Der Kreislaufverbund -------------------------------------------

          Nur bei den Bowl-Anlagen. Ein Kreislaufverbund hat auf Geräteebene
          KEINEN Wirkungsgrad — er entsteht erst im Verbund zweier Geräte über
          den Glykolkreis. Deshalb stand hier lange nichts, und die Heizlast
          rechnete diese Anlagen mit voller Außenluft.

          Das Bild zeigt die Struktur und nicht eine Zuordnung: Welches
          körperliche Gerät auf welchem Platz sitzt, geht aus den
          Typdatenblättern nicht hervor. Wer es als Zuordnung zeichnete, würde
          eine Genauigkeit behaupten, die die Quelle nicht hergibt. */}
      {d.kvs && (
        <ContentSection titel="Wärmerückgewinnung — Kreislaufverbund">
          <KpiReihe>
            <KpiCard
              titel="Rückwärmzahl trocken" wert={d.kvs.systemgradProzent}
              einheit="%" zustand="gut" zustandText="im Verbund erreicht"
            />
            <KpiCard
              titel="Energiewirkungsgrad" wert={d.kvs.energiegradProzent} einheit="%"
            />
            <KpiCard titel="Gruppe" wert={d.kvs.gruppe} />
            <KpiCard
              titel="Abluft-Austritt belegt" wert={d.kvs.frostAustrittC} einheit="°C"
            />
          </KpiReihe>

          <KvsBild kvs={d.kvs} />

          {d.kvs.quelle && <p className="lue-anl-quelle">{d.kvs.quelle}</p>}

          {/* Die Register des zugehörigen Typdatenblatts. IstWrg trennt
              Rückgewinnung von Klimatisierung — beim 100.000er stehen
              1.163 kW Change-Over gegen 700 kW echte Rückgewinnung. Wer das
              vermischt, hält eine Kältemaschinenleistung für Rückgewinnung. */}
          {d.kvs.register?.length ? (
            <DataTable
              name="Register des Typdatenblatts"
              beschreibung="Die Spalte WRG trennt Rückgewinnung von Klimatisierung — wer beides addiert, hält eine Kältemaschinenleistung für zurückgewonnene Wärme."
              spalten={[
                {
                  schluessel: 'wrg', titel: 'WRG', art: 'status', breite: '110px',
                  wert: (r: Register) => (r.istWrg ? 'WRG' : OHNE_WERT),
                  inhalt: (r: Register) => (r.istWrg
                    ? <StatusBadge zustand="gut" text="WRG" klein />
                    : <span className="lue-leise">{OHNE_WERT}</span>),
                },
                {
                  schluessel: 'komponente', titel: 'Komponente', immerSichtbar: true,
                  wert: (r: Register) => r.komponente,
                },
                { schluessel: 'luftart', titel: 'Luftart', wert: (r: Register) => r.luftart },
                {
                  schluessel: 'leistung', titel: 'Nennleistung', art: 'zahl',
                  einheit: 'kW', wert: (r: Register) => r.nennleistungKw,
                },
                {
                  schluessel: 'luft', titel: 'Luft ein → aus', einheit: '°C',
                  wert: (r: Register) => r.eintrittC,
                  inhalt: (r: Register) => (
                    <span className="lue-leise">
                      <WertEinheit wert={r.eintrittC ?? null} /> →{' '}
                      <WertEinheit wert={r.austrittC ?? null} />
                      {r.glykolProzent ? `, ${r.glykolProzent} % Glykol` : ''}
                    </span>
                  ),
                },
              ]}
              zeilen={d.kvs.register}
              schluessel={(r: Register) => `${r.komponente}|${r.luftart}|${r.betriebspunkt ?? ''}`}
              suche={false}
              spaltenwahl={false}
              dicht
            />
          ) : null}
        </ContentSection>
      )}

      {/* Die Unterlagen. Öffentlich nur lesend: Wer über den Verweis kommt,
          soll das PDF öffnen können, aber keines hochladen. */}
      {d.datenblatt?.datenblattVerweis && (
        <div className="lue-anl-unterlagen">
          <a href={d.datenblatt.datenblattVerweis} target="_blank" rel="noreferrer">
            Technisches Datenblatt öffnen
            {d.datenblatt.dateiname && ` (${d.datenblatt.dateiname})`}
          </a>
          {d.datenblatt.herstellerUrl && (
            <>
              {' · '}
              <a href={d.datenblatt.herstellerUrl} target="_blank" rel="noreferrer">
                Herstellerseite
              </a>
            </>
          )}
        </div>
      )}

      {intern && d.verweis && (
        <div className="lue-anl-verweis">
          Öffentlicher Verweis für das Modell:
          <div className="lue-anl-verweis-text">{d.verweis}</div>
        </div>
      )}
    </>
  );
}

/**
 * Die Betriebspunkte der wasserseitigen Einheiten.
 *
 * Gegenüberstellung und kein Diagramm: Bei zwei Punkten je Register ist eine
 * Kurve Zierde. Erst mit Teillastdaten verdient sie ihren Platz.
 *
 * Die Mediumtemperaturen stehen ausdrücklich als Vorlauf und Rücklauf. Der
 * Hersteller schreibt Eintritt und Austritt, und der Import bildet es um — die
 * Verwechslung der beiden ist der häufigste Fehler in solchen Auszügen.
 */
function Betriebspunkte({ d }: { d: Anlagendaten }) {
  const punkte = (d.stroeme ?? []).flatMap((s) =>
    (s.einheiten ?? []).flatMap((u) =>
      (u.betriebspunkte ?? []).map((p) => ({ u, p, strom: s.luftstrom }))));

  if (punkte.length === 0) return null;

  return (
    <ContentSection
      titel="Wasserseitige Betriebspunkte"
      beschreibung="Die Mediumtemperaturen stehen als Vorlauf und Rücklauf. Der Hersteller schreibt Eintritt und Austritt; der Import bildet es um."
    >
      <div className="lue-anl-punkte">
        {punkte.map(({ u, p, strom }) => {
          const vl = bp(p.daten, 'bp_medium_vl');
          const rl = bp(p.daten, 'bp_medium_rl');
          const kuehlt = p.modus === 'cooling';
          return (
            <div key={p.id} className="lue-anl-punkt" data-modus={kuehlt ? 'kuehlen' : 'heizen'}>
              <div className="lue-anl-punkt-titel">
                {u.bezeichnung} · {p.modusDe ?? p.modus}
              </div>
              <div className="lue-anl-punkt-luft">
                {strom === 'ZU' ? 'Zuluft' : 'Abluft'}
                {p.stimmig === false && (
                  <StatusBadge zustand="warnung" text="nicht stimmig" klein />
                )}
              </div>
              <Zeile n="Leistung" w={bp(p.daten, 'bp_leistung')} e="kW" />
              <Zeile n="Medium" w={vl && rl ? `${vl} / ${rl}` : null} e="°C" />
              <Zeile n="Volumenstrom" w={bp(p.daten, 'bp_medium_vs')} e="m³/h" />
              <Zeile n="Druckverlust" w={bp(p.daten, 'bp_medium_dp')} e="kPa" />
              <Zeile n="Glykol" w={bp(p.daten, 'bp_glykol')} e="%" />
              <Zeile n="Kondensat" w={bp(p.daten, 'bp_kondensat')} e="l/h" />
            </div>
          );
        })}
      </div>
    </ContentSection>
  );
}

/**
 * Eine Zeile des Betriebspunkts. Wert und Einheit stehen in ZWEI Elementen
 * nebeneinander (Abschnitt 15) - vorher standen sie in einer Zeichenkette.
 */
function Zeile({ n, w, e }: { n: string; w: string | null; e?: string }) {
  if (w === null || w === '') return null;
  return (
    <div className="lue-anl-zeile">
      <span className="lue-anl-zeile-name">{n}</span>
      <span>
        <span>{w}</span>
        {e && <span className="lue-anl-einheit">{e}</span>}
      </span>
    </div>
  );
}

// ═══ Die zwei Rahmen ═════════════════════════════════════════════════════════

/** In der Anwendung, mit Anmeldung. */
export function AnlagenseiteIntern({ api }: { api: ApiClient }) {
  const { plantId } = useParams<{ plantId: string }>();
  const [d, setD] = useState<Anlagendaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!plantId) return;
    api.anlagenseite(plantId).then(setD)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, plantId]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Anlagendaten konnten nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!d) return <LoadingState text="Anlagendaten werden geladen …" zeilen={8} spalten={3} />;

  return <Inhalt d={d} intern />;
}

/**
 * Öffentlich, über den signierten Verweis aus Revit.
 *
 * Eigener Rahmen ohne Kopfzeile und Navigation — jeder Verweis darin führte ins
 * Leere. Und ein eigener Abruf ohne den ApiClient: Der hängt an MSAL, und hier
 * liegt kein Token vor.
 */
export function AnlagenseiteOeffentlich() {
  const { plantId } = useParams<{ plantId: string }>();
  const s = new URLSearchParams(useLocation().search).get('s');
  const [d, setD] = useState<Anlagendaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!plantId || !s) { setFehler('Kein gültiger Verweis.'); return; }
    fetch(`/api/oeffentlich/anlage/${plantId}?s=${encodeURIComponent(s)}`)
      .then((a) => {
        // NotFound heißt hier zweierlei: Die Kennung gibt es nicht, ODER die
        // Signatur stimmt nicht. Das ist Absicht — wer keinen gültigen Verweis
        // hat, soll nicht erfahren, ob die Anlage existiert.
        if (!a.ok) throw new Error('Dieser Verweis ist nicht gültig.');
        return a.json() as Promise<Anlagendaten>;
      })
      .then(setD)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [plantId, s]);

  return (
    <div className="lue-oeffentlich">
      <div className="lue-oeffentlich-kopf">
        TEUBER+VIEL · Anlagendaten
      </div>
      {fehler
        ? <ErrorState meldung="Dieser Verweis führt zu keiner Anlage." technisch={fehler} />
        : !d
          ? <LoadingState text="Anlagendaten werden geladen …" zeilen={8} spalten={3} />
          : <Inhalt d={d} intern={false} />}
    </div>
  );
}
